import React, { useEffect, useLayoutEffect, useRef, useState, useMemo } from 'react';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import cx from 'classnames';
import { TEXT_ELEMENT_MODULES, TEXT_ELEMENT_FORMATS } from '../../from-builder/elements/text-element';
import { useIsInitFormPage } from '../../../hook/pages/useIsInitFormPage';
import { replaceValuesInText } from '../../../utility';
import { hasFloatingToolbar } from '../../../utility/textFieldLayout';
import { EditorToolbarToggle } from '../../from-builder/elements/editor-toolbar-toggle';
import { GridYValue } from '../../../constants';

const Font = Quill.import('formats/font');
Font.whitelist = ['arial', 'times-new-roman'];
Quill.register(Font, true);

// What an empty line of a text field holds: spaces, the no-break spaces the constructor
// types, and the zero-width characters Quill pads a line with.
const BLANK_TEXT = /^[\s\u00A0\u200B\uFEFF]*$/;
// Things drawn in a text field that carry no text of their own: pictures, rules, and the
// bullet or number of a list item.
const CONTENT_ELEMENTS = new Set(['IMG', 'VIDEO', 'IFRAME', 'HR', 'SVG', 'CANVAS', 'LI']);

// Spaces still show when they are underlined, struck through or highlighted - the
// "Name: ______" lines a form leaves to be filled in by hand.
const isDrawnBlank = (node, area) => {
  for (let elem = node.parentElement; elem && elem !== area; elem = elem.parentElement) {
    const style = window.getComputedStyle(elem);
    const decoration = style.textDecorationLine || style.textDecoration || '';

    if ((decoration && !decoration.startsWith('none')) || !['', 'transparent', 'rgba(0, 0, 0, 0)'].includes(style.backgroundColor)) {
      return true;
    }
  }

  return false;
};

// Whether any of the text lies past the bottom edge of its box - a character, a picture,
// a bullet, a drawn blank - rather than just the blank lines after it and the editor's
// own bottom padding, which leave nothing to scroll to.
//
// Measured with no scrollbar in the box. A scrollbar that takes room (the classic kind)
// narrows the text, the narrower text wraps onto more lines, and those lines overflow in
// turn: a text a single pixel too tall for its box ended up with whole lines hidden
// under its own scrollbar, and so looked like it needed it.
//
// RenderForm asks the same of a printed text field: one that hides text here prints in
// full (see `isPrintExpanded`).
export const hidesContent = (area) => {
  const { overflowY } = area.style;
  area.style.overflowY = 'hidden';

  try {
    if (area.scrollHeight <= area.clientHeight) return false;

    // Nothing to measure the text with: keep the scrollbar rather than risk hiding text.
    if (typeof document.createRange().getBoundingClientRect !== 'function') return true;

    // Measured from the top of the text, so a box the reader has scrolled measures the same.
    const textTop = area.getBoundingClientRect().top + area.clientTop - area.scrollTop;
    const textBottom = textContentBottom(area);

    // Half a pixel of slack for the fractional line heights a larger font size brings.
    return textBottom !== null && textBottom - textTop > area.clientHeight + 0.5;
  } finally {
    area.style.overflowY = overflowY;
  }
};

// Where the text in `area` ends: the bottom of its last character, picture, list item or
// drawn blank, in viewport px - the blank lines after it and the editor's padding left out.
// Null when it shows nothing.
export const textContentBottom = (area) => {
  const range = document.createRange();
  const walker = document.createTreeWalker(area, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  let textBottom = null;

  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    let rect = null;

    if (node.nodeType === Node.TEXT_NODE) {
      if (!node.textContent || (BLANK_TEXT.test(node.textContent) && !isDrawnBlank(node, area))) continue;

      range.selectNodeContents(node);
      rect = range.getBoundingClientRect();
    } else if (CONTENT_ELEMENTS.has(node.tagName.toUpperCase())) {
      rect = node.getBoundingClientRect();
    }

    if (rect && rect.height) {
      textBottom = Math.max(textBottom ?? rect.bottom, rect.bottom);
    }
  }

  return textBottom;
};

const space = '\u0020';

export const TextElement = function FromElement({
  element,
  handlerChange,
  removeElement,
  duplicateElement,
  autoFillingVariables,
  submitterType,
  submitterInfo,
  isPrint,
  isPrintExpanded = false,
  isMobileView = false,
}) {
  const isInitFormPage = useIsInitFormPage();
  const quillRef = useRef(null);
  const { initiator_can_edit, value: elementOptionsValue, valueInput } = element?.options ?? {};
  const currentUserCanEdit = isInitFormPage && initiator_can_edit;
  const htmlAreaRef = useRef();
  const [value, setValue] = useState(isInitFormPage ? elementOptionsValue : valueInput || elementOptionsValue);
  const [showEditor, setShowEditor] = useState(true);

  useEffect(() => {
    if (isInitFormPage) return;
    // const newValue = replaceValuesInText(value, autoFillingVariables, submitterType, submitterInfo);
    // setValue(newValue);
    // handlerChange(element, newValue);
  }, []);

  useLayoutEffect(() => {
    const propsValue = isInitFormPage ? elementOptionsValue : valueInput || elementOptionsValue;
    if (propsValue) {
      const newValue = propsValue.replace(/&nbsp;/g, space);
      setValue(newValue);
    }
  }, [isInitFormPage]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();

    const handleSpace = (event) => {
      if (editor) {
        if (event.key === ' ') {
          event.preventDefault();
          const range = editor.getSelection();
          if (range) {
            editor.insertText(range.index, space);
            editor.setSelection(range.index + 1);
            const newValue = editor.root.innerHTML;
            setValue(newValue);
          }
        }
      }
    };

    editor?.root?.addEventListener('keydown', handleSpace);
    return () => editor?.root?.removeEventListener('keydown', handleSpace);
  }, []);

  const handelEdit = (e) => {
    setValue(e);
    handlerChange(element, e);
  };

  const getQuillClassName = () => {
    const editorWidth = quillRef.current?.editingArea?.offsetWidth;

    if (!editorWidth || editorWidth >= 661) {
      return 'quill-lg';
    }

    if (editorWidth >= 343) {
      return 'quill-md';
    }

    if (editorWidth >= 272) {
      return 'quill-sm';
    }

    if (editorWidth >= 200) {
      return 'quill-xs';
    }

    return 'quill-lg';
  };

  const getHtmlAreaClassName = () => {
    const htmlAreaWidth = htmlAreaRef.current?.offsetWidth;

    if (!htmlAreaWidth || htmlAreaWidth >= 661) {
      return 'quill-lg';
    }

    if (htmlAreaWidth >= 343) {
      return 'quill-md';
    }

    if (htmlAreaWidth >= 272) {
      return 'quill-sm';
    }

    if (htmlAreaWidth >= 200) {
      return 'quill-xs';
    }

    return 'quill-lg';
  };
  // A field on the floating-toolbar layout is edited the way it is drawn on the canvas:
  // the toolbar hangs above the field instead of standing between the field's top edge
  // and its text, so the text the initiator types sits exactly where the read-only form
  // and the print put it, and hiding the toolbar leaves no gap over it. The room the
  // toolbar hangs in is reserved by the field itself (init-form/form-item.js).
  const isFloatingToolbar = hasFloatingToolbar(element);
  const [editorContainer, setEditorContainer] = useState(null);

  useEffect(() => {
    if (!isFloatingToolbar || !currentUserCanEdit) return;

    setEditorContainer(quillRef.current?.getEditor()?.root?.parentNode ?? null);
  }, [isFloatingToolbar, currentUserCanEdit]);

  const cleanValue = useMemo(() => {
    if (isPrint) {
      return replaceValuesInText(value, autoFillingVariables, 'skip', 'skip', true);
    }
    return value;
  }, [isPrint, value]);

  // The constructor lays the text out inside an editor box exactly `innerElemHeight`
  // tall (from-builder/elements/text-element.js), so text that does not fit gets a
  // scrollbar there - and that scrollbar takes 15px off the width the text wraps at.
  // Reading the text back into a box of a different height let the scrollbar appear on
  // one surface and not the other: the same text then wrapped differently, and every
  // paragraph past the first re-wrap sat higher here than on the canvas - by ~130px at
  // the end of a long document - dragging the whole document out from under the fields
  // the author had lined up against it. Give the text the constructor's box (its
  // height, the 1px border its .ql-container carries, and its overflow) and both
  // surfaces wrap it the same way.
  //
  // A field authored before the floating toolbar counted the toolbar's height inside
  // `innerElemHeight`, so pinning its text to that would cut the last lines off. Those
  // keep the box that grows with the text, the way they have always been drawn.
  //
  // A text that would scroll prints in full (the finished-submission view, the print
  // preview): the box keeps the constructor's height as its least and grows to the text.
  //
  // The phone layout wraps the text at the phone's width, not the canvas's, so the
  // constructor's height means nothing there: fields stack one under another and the box
  // is as tall as its text, with nothing to scroll.
  const textAreaStyle =
    !isMobileView && isFloatingToolbar && element.options?.innerElemHeight
      ? { [isPrintExpanded ? 'minHeight' : 'height']: element.options.innerElemHeight * GridYValue }
      : undefined;

  // A box an author made shorter than its text scrolls on purpose, here as on the canvas.
  // But a box whose text all fits, with only the blank lines after it and the editor's
  // bottom padding sticking out, has nothing to scroll to: it is cut off clean instead.
  // Fields saved at a height a few pixels short of their text - the shortest a text
  // field can be is 11 rows, less than a heading followed by empty lines needs, and a
  // long text fitted to the pixel on one screen can come out a pixel taller on another -
  // showed a scrollbar on the form that the text itself never called for. The box
  // starts without one, so it never gets to narrow the text before it is measured.
  const [isScrollable, setIsScrollable] = useState(false);

  useLayoutEffect(() => {
    const area = htmlAreaRef.current;
    if (!area) return undefined;

    let isCurrent = true;
    const update = () => {
      if (isCurrent) setIsScrollable(hidesContent(area));
    };

    update();

    // The text takes its final size only once the field has its width and the fonts are in.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(area);
    document.fonts?.ready?.then(update);

    return () => {
      isCurrent = false;
      observer?.disconnect();
    };
  }, [value, currentUserCanEdit, textAreaStyle?.height, isPrintExpanded]);

  return (
    <div className={'element-wrapper'}>
      <div className={cx('element-inner', { 'fb-text-element': isFloatingToolbar })}>
        {currentUserCanEdit ? (
          <>
            {isFloatingToolbar ? (
              <EditorToolbarToggle container={editorContainer} isToolbarShown={showEditor} onToggle={() => setShowEditor(!showEditor)} />
            ) : (
              <div>
                <button className="btn-link mb-4 ml-auto" onClick={() => setShowEditor(!showEditor)}>
                  {!showEditor && 'Show Editor'}
                  {showEditor && 'Hide Editor'}
                </button>
              </div>
            )}
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={cleanValue}
              modules={TEXT_ELEMENT_MODULES}
              formats={TEXT_ELEMENT_FORMATS}
              className={cx(getQuillClassName(), { hidden: !showEditor })}
              onChange={handelEdit}
            />
          </>
        ) : (
          <div className="html-area-box" style={textAreaStyle}>
            <div
              className={cx(getHtmlAreaClassName(), 'html-area ql-editor', { 'html-area-no-scroll': !isScrollable })}
              dangerouslySetInnerHTML={{ __html: value }}
              ref={htmlAreaRef}
            />
          </div>
        )}
      </div>

      {/* {currentUserCanEdit && (
        <div className={'init-form-aside-buttons'}>
          <button onClick={() => duplicateElement(element.id)} className="btn btn-settings btn-icon init-form-copy-element-btn">
            <i className="icon-copy" />
          </button>
          <button onClick={() => removeElement(element.id)} className="btn btn-settings btn-icon btn-remove">
            <i className="icon-remove"></i>
          </button>
        </div>
      )} */}
    </div>
  );
};
